/**
 * Utilitário de rastreamento para Meta Pixel, Conversion API e Google Tag Manager.
 */

import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    dataLayer: any[];
    fbq: any;
  }
}

interface TrackingData {
  email?: string;
  name?: string;
  phone?: string;
  external_id?: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
  [key: string]: any;
}

/**
 * Gera um ID único para cada evento para desduplicação (Browser vs CAPI)
 */
export const generateEventId = () => {
  return crypto.randomUUID();
};

/**
 * Hash de dados sensíveis (SHA-256) conforme exigido pela Meta
 */
const hashData = async (data: string) => {
  if (!data) return undefined;
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Envia evento para o DataLayer do GTM
 */
export const pushToDataLayer = (eventName: string, data: any = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...data,
    });
    console.log(`[GTM] Event pushed: ${eventName}`, data);
  }
};

/**
 * Envia evento para o Meta Pixel (Browser)
 */
export const trackMetaPixel = (eventName: string, eventId: string, data: any = {}) => {
  if (window.fbq) {
    if (["PageView", "ViewContent", "Lead", "CompleteRegistration", "StartTrial", "InitiateCheckout", "Purchase"].includes(eventName)) {
      window.fbq("track", eventName, data, { eventID: eventId });
    } else {
      window.fbq("trackCustom", eventName, data, { eventID: eventId });
    }
    console.log(`[Meta Pixel] Event tracked: ${eventName}`, { ...data, eventID: eventId });
  }
};

/**
 * Envia evento para o Meta Conversion API via Edge Function
 */
export const trackMetaCAPI = async (eventName: string, eventId: string, data: TrackingData) => {
  try {
    const userData: any = {};
    
    if (data.email) userData.em = await hashData(data.email);
    if (data.name) userData.fn = await hashData(data.name);
    if (data.phone) userData.ph = await hashData(data.phone);
    if (data.external_id) userData.external_id = data.external_id;

    const customData: any = {};
    if (data.value) customData.value = data.value;
    if (data.currency) customData.currency = data.currency || "BRL";
    if (data.transaction_id) customData.order_id = data.transaction_id;

    const { error } = await supabase.functions.invoke("meta-capi", {
      body: {
        event_name: eventName,
        event_id: eventId,
        user_data: userData,
        custom_data: customData,
        event_source_url: window.location.href,
      },
    });

    if (error) throw error;
    console.log(`[Meta CAPI] Event sent: ${eventName}`, { eventId });
  } catch (err) {
    console.error(`[Meta CAPI] Error sending event ${eventName}:`, err);
  }
};

/**
 * Rastreia um evento completo em todas as plataformas (GTM, Pixel, CAPI)
 */
export const trackFullEvent = async (eventName: string, data: TrackingData = {}) => {
  const eventId = generateEventId();
  
  // 1. GTM DataLayer (Standard and Custom events)
  const gtmEventName = eventName.toLowerCase().replace(/([A-Z])/g, "_$1").toLowerCase();
  pushToDataLayer(gtmEventName, { ...data, event_id: eventId });

  // 2. Meta Pixel (Browser)
  trackMetaPixel(eventName, eventId, data);

  // 3. Meta CAPI (Server-to-Server)
  // Fazemos o CAPI de forma assíncrona para não bloquear a UI
  trackMetaCAPI(eventName, eventId, data);
};
